/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// State
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';
import {
	TAdditionalityProject,
	TAdditionalityProjectQueryParams,
} from '../types';
import { TParams } from '@global-types/index';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IAdditionalityProjectSlice {
	/* Pagination */
	additionalityProjectsTotalPages: number;
	additionalityProjectsPage: number;
	additionalityProjectsTotal: number;
	additionalityProjectsPageSize: number;
	additionalityProjectsQueryParams: TParams;

	addAdditionalityProjectFormOpened: boolean;
	updateAdditionalityProjectFormOpened: boolean;
	technicalDocuments: string[];

	idsLogoToDelete: string[];
	idsCoverUrlToDelete: string[];
	idsPublicTechnicalDocumentsToDelete: string[];
	initialAdditionalityData: TAdditionalityProject | null;
	setInitialAdditionalityData: (
		_initialAdditionalityData: TAdditionalityProject
	) => void;

	setIdsLogoToDelete: (
		_idsLogoToDelete: string[]
	) => void;
	setIdsCoverUrlToDelete: (
		_idsCoverUrlToDelete: string[]
	) => void;
	setIdsPublicTechnicalDocumentsToDelete: (
		_idsPublicTechnicalDocumentsToDelete: string[]
	) => void;

	setPublicTechnicalDocuments: (
		_technicalDocuments: string[]
	) => void;

	setAddAdditionalityProjectFormOpened: (
		_addAdditionalityProjectFormOpened: boolean
	) => void;

	setUpdateAdditionalityProjectFormOpened: (
		_updateAdditionalityProjectFormOpened: boolean
	) => void;

	/* Datatable */
	setAdditionalityProjectsPage: (
		_additionalityProjectsPage: number
	) => void;

	setAdditionalityProjectsQueryParams: (
		_additionalityProjectsQueryParams: TAdditionalityProjectQueryParams
	) => void;

	setAdditionalityProjectsPageSize: (
		_pageSize: number
	) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

const initialState = {
	/* Pagination */
	additionalityProjectsTotal: 0,
	additionalityProjectsPage: 1,
	additionalityProjectsPageSize: 50,
	additionalityProjectsTotalPages: 0,
	/**************/
	additionalityProjectsQueryParams: {},

	addAdditionalityProjectFormOpened: false,
	updateAdditionalityProjectFormOpened: false,
	technicalDocuments: [],

	idsLogoToDelete: [],
	idsCoverUrlToDelete: [],
	idsPublicTechnicalDocumentsToDelete: [],
	initialAdditionalityData: null,
};

export const createAdditionalityProjectsSlice: StateCreator<
	TStore,
	[],
	[],
	IAdditionalityProjectSlice
> = (set) => ({
	...initialState,
	setInitialAdditionalityData: (
		_initialAdditionalityData: TAdditionalityProject
	) =>
		set({
			initialAdditionalityData: _initialAdditionalityData,
		}),
	setIdsLogoToDelete: (_idsLogoToDelete: string[]) =>
		set({
			idsLogoToDelete: _idsLogoToDelete,
		}),
	setIdsCoverUrlToDelete: (
		_idsCoverUrlToDelete: string[]
	) =>
		set({
			idsCoverUrlToDelete: _idsCoverUrlToDelete,
		}),
	setIdsPublicTechnicalDocumentsToDelete: (
		_idsPublicTechnicalDocumentsToDelete: string[]
	) =>
		set({
			idsPublicTechnicalDocumentsToDelete:
				_idsPublicTechnicalDocumentsToDelete,
		}),

	setPublicTechnicalDocuments: (
		_technicalDocuments: string[]
	) =>
		set({
			technicalDocuments: _technicalDocuments,
		}),
	setAddAdditionalityProjectFormOpened: (
		_addAdditionalityProjectFormOpened: boolean
	) =>
		set({
			addAdditionalityProjectFormOpened:
				_addAdditionalityProjectFormOpened,
		}),

	setUpdateAdditionalityProjectFormOpened: (
		_updateAdditionalityProjectFormOpened: boolean
	) =>
		set({
			updateAdditionalityProjectFormOpened:
				_updateAdditionalityProjectFormOpened,
		}),

	setAdditionalityProjectsQueryParams: (
		_queryParams: TAdditionalityProjectQueryParams
	) =>
		set({
			additionalityProjectsQueryParams: _queryParams,
		}),
	setAdditionalityProjectsPage: (_page: number) =>
		set({ additionalityProjectsPage: _page }),
	setAdditionalityProjectsPageSize: (_pageSize: number) =>
		set({ additionalityProjectsPageSize: _pageSize }),
});
